import { NotificationSeverity } from '@/components/notifications/Notification';
import { useNotification } from '@/hooks';

import {
  useApproveIntegrationRequestDocMutation,
  useCancelIntegrationRequestMutation,
  useMarkIntegrationRequestSentMutation,
  useObserveIntegrationRequestMutation,
} from '../../../services';
import { apiErrorMessage } from '../config';
import type { Confirm } from './useConfirmDialog';

const CANCEL_CONFIRMATION = {
  title: 'Cancelar solicitud',
  message: 'Se cancela la solicitud y TI no la va a procesar.',
  confirmLabel: 'Cancelar solicitud',
  cancelLabel: 'Volver',
};

const useIntegrationRequestActions = (requestId: string, confirm: Confirm) => {
  const { showNotification } = useNotification();
  const [cancelRequest, { isLoading: isCancelling }] = useCancelIntegrationRequestMutation();
  const [observeRequest, { isLoading: isObserving }] = useObserveIntegrationRequestMutation();
  const [approveRequestDoc, { isLoading: isApproving }] = useApproveIntegrationRequestDocMutation();
  const [markRequestSent, { isLoading: isMarkingSent }] = useMarkIntegrationRequestSentMutation();

  const run = async (
    action: () => Promise<unknown>,
    successMessage: string,
    errorMessage: string
  ): Promise<boolean> => {
    try {
      await action();
      showNotification(successMessage, NotificationSeverity.success);
      return true;
    } catch (error) {
      showNotification(apiErrorMessage(error, errorMessage), NotificationSeverity.error);
      return false;
    }
  };

  const cancel = async () => {
    if (!(await confirm(CANCEL_CONFIRMATION))) return false;
    return run(
      () => cancelRequest(requestId).unwrap(),
      'Solicitud cancelada',
      'No se pudo cancelar la solicitud'
    );
  };

  const observe = (observation: string) =>
    run(
      () => observeRequest({ requestId, observation }).unwrap(),
      'Solicitud devuelta a comercial',
      'No se pudo observar la solicitud'
    );

  const approveDoc = () =>
    run(
      () => approveRequestDoc(requestId).unwrap(),
      'Documentación aprobada: comercial ya la puede enviar',
      'No se pudo aprobar la documentación'
    );

  const markSent = () =>
    run(
      () => markRequestSent(requestId).unwrap(),
      'Solicitud marcada como enviada al cliente',
      'No se pudo marcar como enviada al cliente'
    );

  return {
    cancel,
    observe,
    approveDoc,
    markSent,
    isObserving,
    isBusy: isCancelling || isObserving || isApproving || isMarkingSent,
  };
};

export default useIntegrationRequestActions;
